import type { DMI, IchimokuCloud, MACD } from '../../schemas/indicatortypes.ts';
import { MovingAverages } from './movingAverages';

// Trend Indicators
export class TrendIndicators {
    /**
     * Calculate MACD (Moving Average Convergence Divergence)
     */
    static calculateMACD(prices: number[]): MACD[] {
        const ema12 = MovingAverages.calculateEMA(prices, 12);
        const ema26 = MovingAverages.calculateEMA(prices, 26);
        const macd: MACD[] = [];

        // Calculate MACD line
        const macdLine: number[] = [];
        for (let i = 0; i < Math.min(ema12.length, ema26.length); i++) {
            macdLine.push(ema12[i] - ema26[i]);
        }

        // Calculate signal line (9-period EMA of MACD line)
        const signalLine = MovingAverages.calculateEMA(macdLine, 9);

        // Calculate histogram
        for (let i = 0; i < Math.min(macdLine.length, signalLine.length); i++) {
            macd.push({
                macd: macdLine[i],
                signal: signalLine[i],
                histogram: macdLine[i] - signalLine[i],
            });
        }

        return macd;
    }

    /**
     * Calculate ADX (Average Directional Index)
     */
    static calculateADX(prices: number[], highs: number[], lows: number[]): number[] {
        if (prices.length < 14) return [];

        const period = 14;
        const adx: number[] = [];

        // Calculate True Range and Directional Movement
        const trueRanges: number[] = [];
        const plusDM: number[] = [];
        const minusDM: number[] = [];

        // First TR and DM values
        trueRanges.push(highs[0] - lows[0]);
        plusDM.push(0);
        minusDM.push(0);

        // Calculate TR and DM for each period
        for (let i = 1; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const prevHigh = highs[i - 1] || prices[i - 1];
            const prevLow = lows[i - 1] || prices[i - 1];
            const prevClose = prices[i - 1];

            // True Range = max(high - low, |high - prevClose|, |low - prevClose|)
            const tr1 = high - low;
            const tr2 = Math.abs(high - prevClose);
            const tr3 = Math.abs(low - prevClose);
            trueRanges.push(Math.max(tr1, tr2, tr3));

            // Directional Movement
            const upMove = high - prevHigh;
            const downMove = prevLow - low;

            if (upMove > downMove && upMove > 0) {
                plusDM.push(upMove);
                minusDM.push(0);
            } else if (downMove > upMove && downMove > 0) {
                plusDM.push(0);
                minusDM.push(downMove);
            } else {
                plusDM.push(0);
                minusDM.push(0);
            }
        }

        // Calculate smoothed TR and DM using Wilder's smoothing
        const smoothedTR: number[] = [];
        const smoothedPlusDM: number[] = [];
        const smoothedMinusDM: number[] = [];

        // First smoothed values (sum of first 'period' values)
        let sumTR = 0;
        let sumPlusDM = 0;
        let sumMinusDM = 0;

        for (let i = 0; i < period; i++) {
            sumTR += trueRanges[i];
            sumPlusDM += plusDM[i];
            sumMinusDM += minusDM[i];
        }

        smoothedTR.push(sumTR);
        smoothedPlusDM.push(sumPlusDM);
        smoothedMinusDM.push(sumMinusDM);

        // Subsequent smoothed values using Wilder's smoothing: Smoothed = Previous - (Previous / period) + Current
        for (let i = period; i < trueRanges.length; i++) {
            const newTR =
                smoothedTR[smoothedTR.length - 1] - smoothedTR[smoothedTR.length - 1] / period + trueRanges[i];
            const newPlusDM =
                smoothedPlusDM[smoothedPlusDM.length - 1] -
                smoothedPlusDM[smoothedPlusDM.length - 1] / period +
                plusDM[i];
            const newMinusDM =
                smoothedMinusDM[smoothedMinusDM.length - 1] -
                smoothedMinusDM[smoothedMinusDM.length - 1] / period +
                minusDM[i];

            smoothedTR.push(newTR);
            smoothedPlusDM.push(newPlusDM);
            smoothedMinusDM.push(newMinusDM);
        }

        // Calculate Directional Indicators (DI+ and DI-)
        const plusDI: number[] = [];
        const minusDI: number[] = [];

        for (let i = 0; i < smoothedTR.length; i++) {
            plusDI.push((smoothedPlusDM[i] / smoothedTR[i]) * 100);
            minusDI.push((smoothedMinusDM[i] / smoothedTR[i]) * 100);
        }

        // Calculate Directional Index (DX)
        const dx: number[] = [];

        for (let i = 0; i < plusDI.length; i++) {
            const diSum = plusDI[i] + minusDI[i];
            const diDiff = Math.abs(plusDI[i] - minusDI[i]);
            dx.push(diSum > 0 ? (diDiff / diSum) * 100 : 0);
        }

        // Calculate Average Directional Index (ADX) using Wilder's smoothing
        if (dx.length < period) return [];

        // First ADX value (average of first 'period' DX values)
        let sumDX = 0;
        for (let i = 0; i < period; i++) {
            sumDX += dx[i];
        }
        adx.push(sumDX / period);

        // Subsequent ADX values using Wilder's smoothing
        for (let i = period; i < dx.length; i++) {
            const newADX = adx[adx.length - 1] - adx[adx.length - 1] / period + dx[i];
            adx.push(newADX);
        }

        return adx;
    }

    /**
     * Calculate DMI (Directional Movement Index)
     */
    static calculateDMI(prices: number[], highs: number[], lows: number[]): DMI[] {
        if (prices.length < 14) return [];

        const period = 14;
        const dmi: DMI[] = [];

        // Calculate True Range and Directional Movement
        const trueRanges: number[] = [];
        const plusDM: number[] = [];
        const minusDM: number[] = [];

        // First TR and DM values
        trueRanges.push(highs[0] - lows[0]);
        plusDM.push(0);
        minusDM.push(0);

        // Calculate TR and DM for each period
        for (let i = 1; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const prevHigh = highs[i - 1] || prices[i - 1];
            const prevLow = lows[i - 1] || prices[i - 1];
            const prevClose = prices[i - 1];

            // True Range = max(high - low, |high - prevClose|, |low - prevClose|)
            const tr1 = high - low;
            const tr2 = Math.abs(high - prevClose);
            const tr3 = Math.abs(low - prevClose);
            trueRanges.push(Math.max(tr1, tr2, tr3));

            // Directional Movement
            const upMove = high - prevHigh;
            const downMove = prevLow - low;

            if (upMove > downMove && upMove > 0) {
                plusDM.push(upMove);
                minusDM.push(0);
            } else if (downMove > upMove && downMove > 0) {
                plusDM.push(0);
                minusDM.push(downMove);
            } else {
                plusDM.push(0);
                minusDM.push(0);
            }
        }

        // Calculate smoothed TR and DM using Wilder's smoothing
        const smoothedTR: number[] = [];
        const smoothedPlusDM: number[] = [];
        const smoothedMinusDM: number[] = [];

        // First smoothed values (sum of first 'period' values)
        let sumTR = 0;
        let sumPlusDM = 0;
        let sumMinusDM = 0;

        for (let i = 0; i < period; i++) {
            sumTR += trueRanges[i];
            sumPlusDM += plusDM[i];
            sumMinusDM += minusDM[i];
        }

        smoothedTR.push(sumTR);
        smoothedPlusDM.push(sumPlusDM);
        smoothedMinusDM.push(sumMinusDM);

        // Subsequent smoothed values using Wilder's smoothing: Smoothed = Previous - (Previous / period) + Current
        for (let i = period; i < trueRanges.length; i++) {
            const newTR =
                smoothedTR[smoothedTR.length - 1] - smoothedTR[smoothedTR.length - 1] / period + trueRanges[i];
            const newPlusDM =
                smoothedPlusDM[smoothedPlusDM.length - 1] -
                smoothedPlusDM[smoothedPlusDM.length - 1] / period +
                plusDM[i];
            const newMinusDM =
                smoothedMinusDM[smoothedMinusDM.length - 1] -
                smoothedMinusDM[smoothedMinusDM.length - 1] / period +
                minusDM[i];

            smoothedTR.push(newTR);
            smoothedPlusDM.push(newPlusDM);
            smoothedMinusDM.push(newMinusDM);
        }

        // Calculate Directional Indicators (DI+ and DI-)
        const plusDI: number[] = [];
        const minusDI: number[] = [];

        for (let i = 0; i < smoothedTR.length; i++) {
            plusDI.push((smoothedPlusDM[i] / smoothedTR[i]) * 100);
            minusDI.push((smoothedMinusDM[i] / smoothedTR[i]) * 100);
        }

        // Calculate Directional Index (DX)
        const dx: number[] = [];

        for (let i = 0; i < plusDI.length; i++) {
            const diSum = plusDI[i] + minusDI[i];
            const diDiff = Math.abs(plusDI[i] - minusDI[i]);
            dx.push(diSum > 0 ? (diDiff / diSum) * 100 : 0);
        }

        // Calculate Average Directional Index (ADX) using Wilder's smoothing
        if (dx.length < period) return [];

        const adx: number[] = [];

        // First ADX value (average of first 'period' DX values)
        let sumDX = 0;
        for (let i = 0; i < period; i++) {
            sumDX += dx[i];
        }
        adx.push(sumDX / period);

        // Subsequent ADX values using Wilder's smoothing
        for (let i = period; i < dx.length; i++) {
            const newADX = adx[adx.length - 1] - adx[adx.length - 1] / period + dx[i];
            adx.push(newADX);
        }

        // Return DMI objects with DI+, DI-, and ADX
        for (let i = 0; i < adx.length; i++) {
            dmi.push({
                plusDI: plusDI[i + period - 1] || 0,
                minusDI: minusDI[i + period - 1] || 0,
                adx: adx[i],
            });
        }

        return dmi;
    }

    /**
     * Calculate Ichimoku Cloud
     */
    static calculateIchimoku(prices: number[], highs: number[], lows: number[]): IchimokuCloud[] {
        if (prices.length < 52) return []; // Need at least 52 periods for full calculation

        const ichimoku: IchimokuCloud[] = [];

        // Calculate Tenkan-sen (Conversion Line) - 9-period high/low average
        const tenkanSen: number[] = [];
        for (let i = 8; i < prices.length; i++) {
            const periodHigh = Math.max(...highs.slice(i - 8, i + 1));
            const periodLow = Math.min(...lows.slice(i - 8, i + 1));
            tenkanSen.push((periodHigh + periodLow) / 2);
        }

        // Calculate Kijun-sen (Base Line) - 26-period high/low average
        const kijunSen: number[] = [];
        for (let i = 25; i < prices.length; i++) {
            const periodHigh = Math.max(...highs.slice(i - 25, i + 1));
            const periodLow = Math.min(...lows.slice(i - 25, i + 1));
            kijunSen.push((periodHigh + periodLow) / 2);
        }

        // Calculate Senkou Span A (Leading Span A) - (Tenkan + Kijun) / 2, shifted 26 periods ahead
        const senkouSpanA: number[] = [];
        for (let i = 0; i < Math.min(tenkanSen.length, kijunSen.length); i++) {
            senkouSpanA.push((tenkanSen[i] + kijunSen[i]) / 2);
        }

        // Calculate Senkou Span B (Leading Span B) - 52-period high/low average, shifted 26 periods ahead
        const senkouSpanB: number[] = [];
        for (let i = 51; i < prices.length; i++) {
            const periodHigh = Math.max(...highs.slice(i - 51, i + 1));
            const periodLow = Math.min(...lows.slice(i - 51, i + 1));
            senkouSpanB.push((periodHigh + periodLow) / 2);
        }

        // Calculate Chikou Span (Lagging Span) - current price shifted 26 periods back
        const chikouSpan: number[] = [];
        for (let i = 26; i < prices.length; i++) {
            chikouSpan.push(prices[i - 26]);
        }

        // Combine all components, accounting for the different array lengths due to shifting
        const minLength = Math.min(
            tenkanSen.length,
            kijunSen.length,
            senkouSpanA.length,
            senkouSpanB.length,
            chikouSpan.length,
        );

        for (let i = 0; i < minLength; i++) {
            ichimoku.push({
                tenkan: tenkanSen[i],
                kijun: kijunSen[i],
                senkouA: senkouSpanA[i],
                senkouB: senkouSpanB[i],
                chikou: chikouSpan[i],
            });
        }

        return ichimoku;
    }

    /**
     * Calculate Parabolic SAR
     */
    static calculateParabolicSAR(prices: number[], highs: number[], lows: number[]): number[] {
        if (prices.length < 2) return [];

        const sar: number[] = [];
        const accelerationFactor = 0.02; // Starting AF
        const maximumAcceleration = 0.2; // Maximum AF

        // Initialize SAR
        let currentSAR = lows[0]; // Start with first low
        let isLong = true; // Start with long position
        let af = accelerationFactor;
        let ep = highs[0]; // Extreme point (highest high for long, lowest low for short)

        sar.push(currentSAR);

        for (let i = 1; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];

            if (isLong) {
                // Long position logic
                if (low < currentSAR) {
                    // SAR penetrated - switch to short
                    isLong = false;
                    currentSAR = ep; // Start SAR at the extreme point
                    ep = low; // New extreme point is the low
                    af = accelerationFactor; // Reset acceleration factor
                } else {
                    // Continue long position
                    if (high > ep) {
                        ep = high; // New extreme point
                        af = Math.min(af + accelerationFactor, maximumAcceleration); // Increase AF
                    }

                    // Calculate new SAR
                    currentSAR = currentSAR + af * (ep - currentSAR);

                    // SAR cannot be above the previous low
                    if (i > 0) {
                        const prevLow = lows[i - 1] || prices[i - 1];
                        currentSAR = Math.min(currentSAR, prevLow);
                    }
                }
            } else {
                // Short position logic
                if (high > currentSAR) {
                    // SAR penetrated - switch to long
                    isLong = true;
                    currentSAR = ep; // Start SAR at the extreme point
                    ep = high; // New extreme point is the high
                    af = accelerationFactor; // Reset acceleration factor
                } else {
                    // Continue short position
                    if (low < ep) {
                        ep = low; // New extreme point
                        af = Math.min(af + accelerationFactor, maximumAcceleration); // Increase AF
                    }

                    // Calculate new SAR
                    currentSAR = currentSAR + af * (ep - currentSAR);

                    // SAR cannot be below the previous high
                    if (i > 0) {
                        const prevHigh = highs[i - 1] || prices[i - 1];
                        currentSAR = Math.max(currentSAR, prevHigh);
                    }
                }
            }

            sar.push(currentSAR);
        }

        return sar;
    }
}
